import React, { useEffect, useState } from 'react';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import styled from 'styled-components';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import { BlackFullWidthBtn } from '../../../shared/component/headless/button/BtnBody';
import { useAuth } from '../../../shared/hooks/useApi/useAuth';
import OptimizedImg from '../../../shared/component/OptimizedImg';
import { DisplayAlignment, DisplaySize, ProjectElementType } from '../../../shared/enum/EnumRepository';
import { StyledImgProps } from '../../../shared/dto/StyleCompRepository';
import { useWorkListActions } from '../../../shared/hooks/useApi/useWorkList';
import { useBulk } from '../../../shared/hooks/useApi/useBulk';
import { UpdateImgReq, UpdatePEEachInBulkReq } from '../../../shared/dto/ReqDtoRepository';
import { useBulkStore } from '../../../shared/store/WorkListStore';
import { useWorkDetailActions } from '../../../shared/hooks/useApi/useWorkDetail';
import { useProjectDetailState } from '../../../shared/hooks/useApi/useProjectDetail';
import { useProjectElementActions, useProjectElementState } from '../../../shared/hooks/useApi/useProjectElement';
import { useWorkViewStore } from '../../../shared/store/WorkViewStore';
import { getImageDimensions, removeFileExtension, removeFileExtensionWithinLimit } from '../../../shared/util/imageUtils';
import { getModalMessage } from '../../../shared/constants/modalMessages';
import belowIcon from '../../../asset/icon/below.png';
import { DOCUMENT_DESCRIPTION_MAX_LENGTH } from '../../../shared/constants/limits';

const BulkCreateModalMobile: React.FC = () => {
  const { user } = useAuth();
  const { standardModal, clearModal } = useModalStore();
  const { createWorkBulk, createDetailBulk, createPEBulk } = useBulk();
  const { updateWorkBulk } = useWorkListActions();
  const { updateWorkDetailBulk } = useWorkDetailActions();
  const { project } = useProjectDetailState();
  const { projectElementList } = useProjectElementState();
  const { updateProjectElementBulk } = useProjectElementActions();
  const { current, total } = useBulkStore();
  const [fileUrls, setFileUrls] = useState<{ [key: string]: string }>({});
  const activeWork = useWorkViewStore((s) => s.activeWork);

  useEffect(() => {
    if (!standardModal || !standardModal.data) return;
    const urls: { [key: string]: string } = {};
    standardModal.data.forEach((file) => {
      urls[file.name] = URL.createObjectURL(file);
    });
    setFileUrls(urls);

    return () => {
      Object.values(urls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [standardModal]);

  if (standardModal == null || !user || !standardModal.data) return null;

  const showParseMessage = standardModal.value === 'list';

  const handleCreateAll = async () => {
    if (!standardModal.data) return;
    const files = standardModal.data;

    const imageDimensions = await Promise.all(
      files.map((file) => getImageDimensions(file))
    );

    if (standardModal.value === 'list') {
      const results = await createWorkBulk(files);
      if (!results) return;
      const workList = results.map((result, i) => ({
        id: result.workId,
        originUrl: result.originUrl,
        width: imageDimensions[i].width,
        height: imageDimensions[i].height,
        title: removeFileExtension(files[i].name),
      })) satisfies UpdateImgReq[];
      await updateWorkBulk({ workList });
    } else if (standardModal.value === 'project-document') {
      if (!project) return;
      const peFiles = files.map((file) => ({ file, peType: ProjectElementType.DOCUMENT }));
      const results = await createPEBulk(peFiles, project.id, projectElementList.length);
      if (!results) return;
      const peList = results.map((result, i) => ({
        peId: result.peId,
        projectElementType: ProjectElementType.DOCUMENT,
        originUrl: result.originUrl,
        width: imageDimensions[i].width,
        height: imageDimensions[i].height,
        description: removeFileExtensionWithinLimit(files[i].name, DOCUMENT_DESCRIPTION_MAX_LENGTH),
      })) satisfies UpdatePEEachInBulkReq[];
      await updateProjectElementBulk(project.id, { peList });
    } else {
      if (!activeWork) return;
      const results = await createDetailBulk(files, activeWork.id);
      if (!results) return;
      const detailList = results.map((result, i) => ({
        id: result.workDetailId,
        originUrl: result.originUrl,
        width: imageDimensions[i].width,
        height: imageDimensions[i].height,
        description: removeFileExtension(files[i].name),
      })) satisfies UpdateImgReq[];
      await updateWorkDetailBulk({ detailList });
    }
  };

  const handleClose = () => {
    clearModal(700);
  };

  return (
    <BulkCreateStationComp>
      <TitleWrapper>
        <TitleText>{getModalMessage('CREATE_BUIK', { title: standardModal.title || '' })}</TitleText>
        <UniversalIconBtn
          size="fit"
          border={false}
          isTransparent
          backgroundImg={belowIcon}
          onClick={handleClose}
        />
      </TitleWrapper>
      {showParseMessage && (
        <ParseMessageWrapper>
          <ParseMessage>{getModalMessage('CREATE_BULK_FILENAME_HINT')}</ParseMessage>
          <ParseMessage>{getModalMessage('CREATE_BULK_FILENAME_FORMAT')}</ParseMessage>
        </ParseMessageWrapper>
      )}
      <LoadingBarContainer>
        <LoadingBar>
          <LoadingBarFill $usagePercentage={(current / (total || 0)) * 100} />
        </LoadingBar>
        <LoadingText>
          {current} / {total}
        </LoadingText>
      </LoadingBarContainer>
      <FileListContainer>
        {standardModal.data?.map((file) => (
          <FileRow key={file.name}>
            <ThumbContainer>
              <ThumbWrapper>
                <OptimizedImg
                  imageUrl={fileUrls[file.name] || ''}
                  alt={file.name}
                  StyledImg={ThumbnailImg}
                  displaySize={DisplaySize.REGULAR}
                  displayAlignment={DisplayAlignment.CENTER}
                  priority={false}
                />
              </ThumbWrapper>
            </ThumbContainer>
            <FileName>{file.name}</FileName>
          </FileRow>
        ))}
      </FileListContainer>
      <SubmitButton onClick={handleCreateAll}>
        Create ALL
      </SubmitButton>
    </BulkCreateStationComp>
  );
};

const BulkCreateStationComp = styled.div`
  width: 100%;
  height: fit-content;
  min-height: 50vh;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
`;

const TitleWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  margin-top: clamp(12px, 2vh, 30px);
  margin-bottom: clamp(20px, 8vh, 30px);
`;

const TitleText = styled.div`
  ${({ theme }) => theme.typography.Body_01_1};
  color: ${({ theme }) => theme.colors.color_Gray_02};
`;


const ParseMessageWrapper = styled.div`
  margin-top: -12px;
  margin-bottom: 16px;
`;

const ParseMessage = styled.div`
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_03};
  line-height: 1.5;
`;

const LoadingBarContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 4px;
  margin-bottom: 16px;
`;

const LoadingBar = styled.div`
  width: 100%;
  height: 4px;
  margin: 6px 0;
  border-top: 0.5px solid ${({ theme }) => theme.colors.color_Gray_05};
  background-color: ${({ theme }) => theme.colors.color_Gray_06};
  border-radius: 1px;
  overflow: hidden;
  position: relative;
`;

const LoadingBarFill = styled.div<{ $usagePercentage: number }>`
  width: ${({ $usagePercentage }) => Math.min($usagePercentage, 100)}%;
  height: 100%;
  background-color: ${({ theme, $usagePercentage }) => {
    if ($usagePercentage >= 95) return theme.colors.color_Gray_01;
    if ($usagePercentage >= 85) return theme.colors.color_Gray_02;
    if ($usagePercentage >= 70) return theme.colors.color_Gray_03;
    if ($usagePercentage >= 50) return theme.colors.color_Gray_04;
    return theme.colors.color_Gray_05;
  }};
  transition: width 0.3s ease;
`;

const LoadingText = styled.div`
  width: 100%;
  text-align: right;
  ${({ theme }) => theme.typography.Body_03_2};
`;

const FileListContainer = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0 8px 0;
  overflow-y: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;

  border-top: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};

  &::-webkit-scrollbar {
    display: none;
  }
`;

const FileRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const ThumbContainer = styled.div`
  flex: 0 0 auto;
  margin-right: 12px;
`;

const ThumbWrapper = styled.div`
  position: relative;
  width: 44px;
  height: 44px;
`;

const ThumbnailImg = styled.img<StyledImgProps>`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const FileName = styled.div`
  flex: 1;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_03};
  word-break: break-all;
`;

const SubmitButton = styled(BlackFullWidthBtn)`
  margin: 20px 0px 15px 0px;
`;

export default BulkCreateModalMobile;
